import { postForBlob, queryNoTracking, update, updateAsFormData } from '../../services/DataService';
import authService from '../../api-authorization/AuthorizeService';
import { foldCasingMissesIntoResult } from '../workflow/workflowApi';
import { clearDocumentTemplateSelectionCache } from '../documents/DocumentTemplateSelection';

/**
 * Mutations that change which DocumentTemplate rows the print dialogs offer (a pdfscript template appears on publish,
 * follows a rename, disappears on delete / deactivation) drop the pickers' cached lists so the change shows without a
 * page reload. Cheap and harmless for the other customization types.
 */
const afterTemplateListChange = (result) => {
  clearDocumentTemplateSelectionCache();
  return result;
};

// Data queries (DataController partial: CustomizationsController.cs)
const q_Customizations_Grid = queryNoTracking('Customizations_Grid', 0);
const q_Customization_VersionsGrid = queryNoTracking('Customization_VersionsGrid', 0);
const q_PdfDocument_DataSourceTypes = queryNoTracking('PdfDocument_DataSourceTypes', 0);
const q_PdfDocument_Sample = queryNoTracking('PdfDocument_Sample', 0);
const q_PdfDocument_RecordTokens = queryNoTracking('PdfDocument_RecordTokens', 0);
const q_PdfDocument_RecordData = queryNoTracking('PdfDocument_RecordData', 0);
const q_Customization_GetDraft = queryNoTracking('Customization_GetDraft', 0);
const q_Customization_GetVersion = queryNoTracking('Customization_GetVersion', 0);
const q_Customization_PublishedStringOverrides = queryNoTracking('Customization_PublishedStringOverrides', 0);
const q_Customization_StringOverrideDescriptors = queryNoTracking('Customization_StringOverrideDescriptors', 0);
const q_Customization_GetAccess = queryNoTracking('Customization_GetAccess', 0);
const q_Customizations_PublishedForMe = queryNoTracking('Customizations_PublishedForMe', 0);
const q_Customization_PublishedDefinition = queryNoTracking('Customization_PublishedDefinition', 0);
const q_CustomizationDataStore_Grid = queryNoTracking('CustomizationDataStore_Grid', 0);
const q_CustomizationDataStore_Get = queryNoTracking('CustomizationDataStore_Get', 0);
const q_CustomizationDataStore_RowStatus = queryNoTracking('CustomizationDataStore_RowStatus', 0);
const q_SupplyOrderIntegration_Contracts = queryNoTracking('SupplyOrderIntegration_Contracts', 0);
const q_SupplyOrderIntegration_Published = queryNoTracking('SupplyOrderIntegration_Published', 0);
const q_SupplyOrderIntegration_MappingProducts = queryNoTracking('SupplyOrderIntegration_MappingProducts', 0);
const q_CustomizationAssets_List = queryNoTracking('CustomizationAssets_List', 0);
const q_CustomizationAsset_Get = queryNoTracking('CustomizationAsset_Get', 0);
const q_CustomizationAsset_UsedBy = queryNoTracking('CustomizationAsset_UsedBy', 0);
const q_CustomizationAsset_SuggestKey = queryNoTracking('CustomizationAsset_SuggestKey', 0);
const q_ProductModels_List = queryNoTracking('ProductModels_List', 0);
const q_ProductModel_Bindable = queryNoTracking('ProductModel_Bindable', 0);

/**
 * Product visualisation: the published productModel customisations a product may bind to
 * ([{ ID, CustomizationID, Name, Description, TypeVersion, LastPublishedDate, IsMulti, Members: [{ Name, Label }] }]).
 * Empty without the RTL_ProductVisualization module.
 */
export async function listProductModels() {
  return await q_ProductModels_List.getAll();
}

/**
 * One published model as the product editor binds it: { CustomizationID, Name, TypeVersion, Members: [{ Name, Label }],
 * Inputs: [{ Input, Label, Type, Member, Default, Min, Max, Options, Unit, IsParam }], Definition (the published JSON the
 * preview renders) } - or null when the model is missing, inactive or unpublished.
 */
export async function getProductModelBindable(customizationID) {
  return await q_ProductModel_Bindable.getFirstOrDefault({ customizationID });
}

export async function listCustomizations() {
  return await q_Customizations_Grid.getAll();
}

export async function getCustomizationVersions(customizationID) {
  return await q_Customization_VersionsGrid.getAll({ id: customizationID });
}

/** Queryable form of the customizations grid for a bound AccentDataGrid (server-side filter/sort/page). */
export function queryCustomizationsGrid() {
  return q_Customizations_Grid.getAllAsQueryable();
}

/**
 * The pdfscript data-source registry (static per build): [{ Type: 'Quote', Code: 'doc_temp_type_quote',
 * LatestVersion: 'V001', Versions: ['V001'] }]. Drives the New pdf-document type picker and the Data source
 * column's "Upgrade available" marker (row.TypeVersion !== LatestVersion for row.SubType).
 */
export async function getPdfDocumentDataSourceTypes() {
  return await q_PdfDocument_DataSourceTypes.getAll();
}

/**
 * The data contract sample for a document type as indented JSON ({ Type, Version, Json }) - what
 * ctx.data looks like to the script - or null when the (type, version) is unknown / retired.
 */
export async function getPdfDocumentSample(type, version = null) {
  const params = version ? { type, version } : { type };
  return await q_PdfDocument_Sample.getFirstOrDefault(params);
}

/**
 * The data store rows a pdfDocument template's dataStore script requests for the chosen test record (the contract
 * sample when recordID is 0): { Rows: [{ Scope, Key, Reference, Description, Optional, IsWildcard, Status, Count }],
 * Note } - statuses only, never a value; Note carries the script's own failure. Throws on a server-side refusal.
 */
export async function getPdfDocumentDataStoreRows({ customizationID, definition, recordID = 0, printSettingID = null, customParams = null }) {
  const result = await update('PdfDocument_DataStoreRows', {
    customizationID: Number(customizationID) || 0,
    definition,
    recordID: Number(recordID) || 0,
    printSettingID: printSettingID ? Number(printSettingID) : null,
    customParams: customParams && Object.keys(customParams).length > 0 ? customParams : null,
  }, false);
  if (result?.IsFrameworkException || (result?.Message && !result?.Rows)) {
    throw new Error(typeof result?.Message === 'string' ? result.Message : 'The data store rows could not be read.');
  }
  return { Rows: Array.isArray(result?.Rows) ? result.Rows : [], Note: result?.Note ?? null };
}

/**
 * ctx.data for the editor's chosen test record as indented JSON ({ Type, Version, Json }) - what the
 * script really reads for that record, curated by the chosen print setting - or null when the type has
 * no record path or the record cannot be built (the caller falls back to the contract sample).
 */
export async function getPdfDocumentRecordData(type, recordID, version = null, printSettingID = null) {
  const params = { type, recordID: Number(recordID) || 0 };
  if (version) params.version = version;
  if (printSettingID) params.printSettingID = Number(printSettingID);
  return await q_PdfDocument_RecordData.getFirstOrDefault(params);
}

/**
 * The record tokens a document type's print-time queries may be scoped by, with the test record's
 * values: [{ Token: 'job.ID', Value: '123' }, { Token: 'company.ID', Value: '' }, ...] (a linked
 * record the job lacks has an empty Value). Empty when the type has no record path.
 */
export async function getPdfDocumentRecordTokens(type, recordID) {
  return await q_PdfDocument_RecordTokens.getAll({ type, recordID: Number(recordID) || 0 });
}

export async function getCustomizationDraft(customizationID) {
  return await q_Customization_GetDraft.getFirstOrDefault({ id: customizationID });
}

export async function getCustomizationVersion(customizationID, versionID) {
  return await q_Customization_GetVersion.getFirstOrDefault({ id: customizationID, versionID });
}

/**
 * The tenant's published string overrides, already resolved by the server through
 * its descriptor catalog to a { <strings-file path>: <replacement text> } map, or
 * null. Server returns null when the RTL_Customization module is not licensed, so
 * overrides can never apply on unlicensed tenants regardless of what is stored.
 */
export async function getPublishedStringOverrides() {
  const result = await q_Customization_PublishedStringOverrides.getFirstOrDefault({});
  const overrides = result?.Overrides ?? result?.overrides;
  return overrides && typeof overrides === 'object' ? overrides : null;
}

/**
 * The catalog of overridable strings ({ Id, Label, DefaultText }) for the management
 * editor. The C# StringOverrideDescriptors catalog is the single source of truth.
 */
export async function getStringOverrideDescriptors() {
  const rows = await q_Customization_StringOverrideDescriptors.getAll();
  return Array.isArray(rows) ? rows : [];
}

/** The customization's access grants: { users: [id], teams: [id], accessRoles: [id] }. */
export async function getCustomizationAccess(customizationID) {
  const result = await q_Customization_GetAccess.getFirstOrDefault({ id: customizationID });
  return {
    users: Array.isArray(result?.Users) ? result.Users : [],
    teams: Array.isArray(result?.Teams) ? result.Teams : [],
    accessRoles: Array.isArray(result?.AccessRoles) ? result.AccessRoles : [],
  };
}

/**
 * The published customizations the current user may consume
 * ([{ ID, Name, Description, CustomizationType }]). Module-gated server-side;
 * non-managers only see customizations whose access grants name them.
 */
export async function listPublishedCustomizationsForMe() {
  const rows = await q_Customizations_PublishedForMe.getAll();
  return Array.isArray(rows) ? rows : [];
}

/**
 * The latest published definition of one accessible customization
 * ({ ID, Name, CustomizationType, Definition }), or null.
 */
export async function getPublishedCustomizationDefinition(customizationID) {
  return await q_Customization_PublishedDefinition.getFirstOrDefault({ id: customizationID });
}

/**
 * Queryable data source for the Data Store management grid: the bound grid composes
 * its column filters, sorting and infinite-scroll paging onto this query server-side.
 * Expired entries are excluded unless includeExpired is set (they are invisible to
 * workflow reads and are removed by the cleanup sweep shortly after expiring).
 */
export function getCustomizationDataStoreGridQuery(searchText, includeExpired = false, { encrypted = false, scope = null } = {}) {
  // Keys are only sent when set: Breeze puts GET parameters on the query string, so a
  // null searchText would otherwise reach the server as the literal text "null".
  const params = {};
  if (searchText) params.searchText = searchText;
  if (includeExpired === true) params.includeExpired = true;
  if (encrypted === true) params.encrypted = true;
  if (scope) params.scope = scope;
  return q_CustomizationDataStore_Grid.getAllAsQueryable(Object.keys(params).length ? params : null);
}

/**
 * One Data Store entry including its value JSON, for the edit dialog. An encrypted entry comes back with
 * IsEncrypted true and ValueJson null: the value is only obtainable through decryptCustomizationDataStoreEntry.
 */
export async function getCustomizationDataStoreEntry(id) {
  return await q_CustomizationDataStore_Get.getFirstOrDefault({ id });
}

/**
 * Existence (never values) of the data store rows a customisation's dataStore output requests: one row per requested
 * key with { Scope, Key, ID, HasValue, IsEncrypted, UpdatedOn, ExpiresOn }.
 */
export async function getCustomizationDataStoreRowStatus(scope, keys) {
  if (!scope || !Array.isArray(keys) || keys.length === 0) return [];
  return await q_CustomizationDataStore_RowStatus.getAll({ scope, keys: keys.join('\n') });
}

/**
 * The supply order integration contract registry (static per build): { LatestVersion, Versions, Outputs,
 * TransportTypes, MaxSamples, DataSourceType } - or null when the caller cannot manage customizations.
 */
export async function getSupplyOrderIntegrationContracts() {
  return await q_SupplyOrderIntegration_Contracts.getFirstOrDefault();
}

/** Published, active supplyOrderIntegration customisations for the supplier screen's picker: [{ ID, CustomizationID, Name, Description, TypeVersion, InterfaceKey }]. */
export async function getPublishedSupplyOrderIntegrations() {
  return await q_SupplyOrderIntegration_Published.getAll();
}

/**
 * Editor preview of a supplyOrderIntegration definition for a test supply job (0 = the contract's sample order):
 * { RecordID, IsSample, OrderNumber, SupplierName, Version, Errors: { output: [..] }, Warnings, Files, SendPlan,
 *   RouteConflict, Samples, Supplier,
 *   DataStoreRows: [{ Scope, Key, Reference, Description, Optional, IsWildcard, Status, Count }]  - what the dataStore
 *     output requested and what the prefetch found (Found | Encrypted | Missing | Sample | Truncated); a test supply job
 *     reads the tenant's real rows, the sample order reads each request's sample,
 *   DataStoreReads: [{ Output, Reference, Found }] - every ctx.dataStore read the scripts made,
 *   SecretTokens: [..] - the $secret{scope:key} tokens the credentials carry (names of encrypted rows, never values) }.
 * Nothing is sent and nothing is decrypted. Throws with the server's reason when the request itself is refused.
 */
export async function previewSupplyOrderIntegration({ customizationID, definition, recordID }) {
  const result = await update('SupplyOrderIntegration_Preview', { customizationID: Number(customizationID) || 0, definition, recordID: Number(recordID) || 0 }, false);
  if (result?.IsFrameworkException || (result?.Message && !result?.Errors)) {
    throw new Error(typeof result?.Message === 'string' ? result.Message : 'The preview could not be run.');
  }
  return result;
}

/**
 * Editor Products tab: the products to check a supplier integration's mapping plan against - the active products the
 * supplier supplies plus the products on the test supply job. Resolves to
 * { SupplierID, SupplierName, Products: [{ ProductID, ProductIntID, ProductName, ProductCode, Active, SuppliedBySupplier, OnTestOrder }] }
 * or null when the supplier is unknown.
 */
export async function getSupplyOrderIntegrationMappingProducts({ supplierID, recordID }) {
  const rows = await q_SupplyOrderIntegration_MappingProducts.getAll({ supplierID: Number(supplierID) || 0, recordID: Number(recordID) || 0 });
  return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
}

/**
 * Editor Products tab: one product's Supplier Mappings for the supplier checked against the definition's mapping plan
 * (read-only; nothing on the product changes). Resolves to
 * { ProductID, ProductIntID, VersionID, ProductName, ProductCode, HasPlan, Applies, Approach, HasMapping,
 *   Product: { Status, MappedName, MappedCode, Message },
 *   Options: [{ ID, Group, Description, Code, DisplayType, MappedCode, MappedName, Status, Message, Values: [{ ID, Description, Code, MappedCode, MappedName, Status, Sends, Note }] }],
 *   UncoveredFields: [..], Counts: { Options, OptionsOk, OptionsUnrecognised, OptionsUnmapped, ValuesMissing, ValuesYours }, Summary, ValueRuleText, HasGaps }
 *   (value Status: ok = something typed on it fires; yourCode / yourText = nothing typed, the chain falls back to the business's own
 *   code / text; missing = nothing fires; notUsed = the plan does not read values or the option has nothing typed yet)
 * or null when the product or supplier is unknown. Throws with the server's reason when the request is refused.
 */
export async function checkSupplyOrderIntegrationProductMapping({ customizationID, definition, supplierID, productID }) {
  const result = await update('SupplyOrderIntegration_MappingCheck', {
    customizationID: Number(customizationID) || 0,
    definition: definition ?? null,
    supplierID: Number(supplierID) || 0,
    productID,
  }, false);
  if (result?.IsFrameworkException || (result?.Message && !result?.Counts)) {
    throw new Error(typeof result?.Message === 'string' ? result.Message : 'The product mapping could not be checked.');
  }
  return result ?? null;
}

/**
 * Send log "Save as sample": the reply a web endpoint transport attempt recorded becomes a captured sample of the
 * supplier's integration draft. Resolves to { CustomizationID, CustomizationName, SampleName, Status, SampleCount }.
 */
export async function saveSupplyOrderIntegrationSample({ ediOutboundFileTransportID, name }) {
  const result = await update('SupplyOrderIntegration_SaveSample', { ediOutboundFileTransportID: Number(ediOutboundFileTransportID) || 0, name: name || null }, true);
  if (result?.IsFrameworkException || (result?.Message && !result?.SampleName)) {
    throw new Error(typeof result?.Message === 'string' ? result.Message : 'The reply could not be saved as a sample.');
  }
  return result;
}

/** One produced file of the dispatch (primary or companion) for the test supply job, as a blob { url, fileName }. */
export async function downloadSupplyOrderIntegrationFile({ customizationID, definition, recordID, fileName }) {
  return await postForBlob('/Download/SupplyOrderIntegrationFile', {
    CustomizationID: Number(customizationID) || 0,
    Definition: definition,
    RecordID: Number(recordID) || 0,
    FileName: fileName,
  });
}

// Updates (UpdateController partial: CustomizationUpdates.cs)
/**
 * subType / typeVersion apply to the pdfDocument type only: subType is the document type
 * ("Quote", ...; required), typeVersion the data-source version (null = latest).
 */
export async function createCustomization({ name, customizationType, definition = null, description = null, subType = null, typeVersion = null }) {
  return await update('Customization_Create', { name, customizationType, definition, description, subType, typeVersion }, true);
}

/**
 * Copies a customization to a new record holding the source's current definition; resolves to the new ID
 * (> 0) or throws with the server's reason. pdfDocument only: `subType` ("Invoice", ...) copies to ANOTHER
 * document type (the script is carried verbatim for the editor's assistant to convert); null keeps the type.
 */
export async function copyCustomization({ sourceID, name, description = null, subType = null }) {
  const result = await update('Customization_Copy', { sourceID, name, description, subType }, true);
  const id = Number(result);
  if (!(id > 0)) {
    throw new Error(typeof result?.Message === 'string' ? result.Message : 'The copy could not be created.');
  }
  return id;
}

/** pdfDocument only: moves the record's data-source version (Upgrade data source flow). */
export async function setCustomizationTypeVersion(customizationID, typeVersion) {
  return await update('Customization_SetTypeVersion', { customizationID, typeVersion }, true);
}

export async function saveCustomizationDraft(customizationID, definition) {
  return await update('Customization_SaveDraft', { customizationID, definition }, true);
}

export async function publishCustomization(customizationID) {
  return afterTemplateListChange(await update('Customization_Publish', { customizationID }, true));
}

/**
 * The type's publish gates over `definition` (the stored draft when null) WITHOUT publishing: { Ok, Problems: [] } -
 * what Publish would refuse with, one entry per problem. Nothing is written. The editor runs it on demand and hands the
 * problems to its assistant; the list view runs it before publishing so a refusal comes as a list, not a bare error.
 */
export async function checkCustomizationPublish({ customizationID, definition = null }) {
  const result = await update('Customization_CheckPublish', { customizationID: Number(customizationID) || 0, definition }, false);
  if (result?.IsFrameworkException || !Array.isArray(result?.Problems)) {
    throw new Error(typeof result?.Message === 'string' ? result.Message : 'The publish check could not be run.');
  }
  return result;
}

export async function restoreCustomizationToVersion(customizationID, versionGuid) {
  return afterTemplateListChange(await update('Customization_RestoreToVersion', { customizationID, customizationVersionID: versionGuid }, true));
}

export async function renameCustomization(customizationID, name, description) {
  return afterTemplateListChange(await update('Customization_Rename', { customizationID, name, description }, true));
}

export async function deleteCustomization(customizationID) {
  return afterTemplateListChange(await update('Customization_Delete', { customizationID }, true));
}

/**
 * Saves a plain Data Store entry. id > 0 replaces that row's stored value JSON (typed-value shape) and expiry; id 0 with
 * scope/key creates the row from raw value JSON (a string in quotes, a number, an object), which the server stores in the
 * typed-value shape readers expect.
 */
export async function saveCustomizationDataStoreEntry({ id = 0, scope = null, key = null, valueJson, expiresOn = null }) {
  return await update('CustomizationDataStore_Save', { id, scope, key, valueJson, expiresOn }, true);
}

/**
 * Writes a secret (encrypted Data Store entry). The server encrypts secretValue; it is never echoed back.
 * id > 0 overwrites that row (an empty secretValue keeps the stored value and only moves the expiry);
 * id 0 with scope/key creates or overwrites the secret at that address.
 */
export async function saveCustomizationSecret({ id = 0, scope = null, key = null, secretValue = '', expiresOn = null }) {
  return await update('CustomizationDataStore_Save', { id, scope, key, isEncrypted: true, secretValue, expiresOn, valueJson: '' }, true);
}

/**
 * Setup users only (enforced server-side and audited): the plaintext of one encrypted entry.
 * Resolves { Success, Value, Error }.
 */
export async function decryptCustomizationDataStoreEntry(id) {
  return await update('CustomizationDataStore_Decrypt', { id }, true, 'Decrypting secret');
}

export async function deleteCustomizationDataStoreEntry(id) {
  return await update('CustomizationDataStore_Delete', { id }, true);
}

/** Replaces the customization's access grants. Empty lists = managers only. */
export async function setCustomizationAccess(customizationID, { users = [], teams = [], accessRoles = [] } = {}) {
  return await update('Customization_SetAccess', { customizationID, users, teams, accessRoles }, true);
}

/**
 * Tests one customization output script IN THE WEB APP (authoring: assistant dialog
 * output tests, manager-only version previews). Same request/response shape as the
 * workflow node-test harness, but gated on customization management — Customization
 * must work without the Workflow feature or the workflow engine service existing.
 */
export async function testCustomizationOutputScript({ name, valueMode, valueTemplate, script, variablesJson, templateValues, localsJson } = {}) {
  const res = await update(
    'Customization_TestOutputScript',
    {
      name: name ?? '',
      valueMode: valueMode ?? 'javascript',
      valueTemplate: valueTemplate ?? '',
      script: script ?? '',
      variablesJson: variablesJson ?? '{}',
      templateValues: templateValues ?? {},
      localsJson: localsJson ?? '{}',
      // This endpoint only serves tests, so casing detection is always on.
      detectCasingMisses: true,
    },
    false
  );
  return foldCasingMissesIntoResult(res);
}

/**
 * Evaluates one output of a PUBLISHED customization for live rendering. Sends only
 * the customization identity, output NAME and runtime context — the server
 * re-validates the caller's access per call and runs the script stored in the
 * published definition, never client-supplied script text.
 */
export async function evaluatePublishedCustomizationOutput({ customizationID, outputName, templateValues, localsJson } = {}) {
  const res = await update(
    'Customization_EvaluatePublishedOutput',
    {
      customizationID: customizationID ?? 0,
      outputName: outputName ?? '',
      templateValues: templateValues ?? {},
      localsJson: localsJson ?? '{}',
    },
    false
  );
  return foldCasingMissesIntoResult(res);
}

// Asset library (Customizations > Assets; CustomizationsController / CustomizationUpdates #region CustomizationAsset)

/** True when an update call came back as the ExceptionMiddleware envelope (the message has already been shown to the user). */
export const isFrameworkError = (res) => !!(res && typeof res === 'object' && res.IsFrameworkException);

/** Every asset of the tenant's library ([CustomizationAssetModel], no bytes), ordered by key. */
export async function listCustomizationAssets() {
  const rows = await q_CustomizationAssets_List.getAll();
  return Array.isArray(rows) ? rows : [];
}

/** One asset (CustomizationAssetModel, text assets include their Text), or null. */
export async function getCustomizationAsset(id) {
  return await q_CustomizationAsset_Get.getFirstOrDefault({ id });
}

/** The document templates that declare the asset ([{ CustomizationID, Name, SubType, Published, Draft }]). */
export async function getCustomizationAssetUsedBy(id) {
  const rows = await q_CustomizationAsset_UsedBy.getAll({ id });
  return Array.isArray(rows) ? rows : [];
}

/** The unique key the server would derive for a filename (the user may still change it before saving). */
export async function suggestCustomizationAssetKey(fileName) {
  const res = await q_CustomizationAsset_SuggestKey.getFirstOrDefault({ fileName: fileName ?? '' });
  return (res?.Key ?? res?.key ?? '').toString();
}

/**
 * Creates an image / pdf asset from a File (id null) or replaces an existing asset's file (id set; the key is kept).
 * Returns the saved CustomizationAssetModel, or the framework-error envelope when the server refused (see isFrameworkError).
 */
export async function uploadCustomizationAsset({ id = null, key = null, description = null, file }) {
  return await updateAsFormData('CustomizationAsset_Upload', { ID: id, Key: key, Description: description, File: file });
}

/**
 * Creates (id null) or updates a text asset's content. `contentType` (text/plain, text/markdown, application/json,
 * text/yaml, text/csv) stores the text verbatim; absent = HTML from the rich-text editor. Returns the saved model or
 * the framework-error envelope.
 */
export async function saveCustomizationAssetText({ id = null, key = null, description = null, text, contentType = null }) {
  return await update('CustomizationAsset_SaveText', { id, key, description, text: text ?? '', contentType }, true);
}

/** Metadata edit (key only while unused, description, active). Returns the saved model or the framework-error envelope. */
export async function updateCustomizationAsset({ id, key = null, description = null, active = null }) {
  return await update('CustomizationAsset_Update', { id, key, description, active }, true);
}

/** Deletes an unused asset (the server refuses, with the template names, when any template declares it). */
export async function deleteCustomizationAsset(id) {
  return await update('CustomizationAsset_Delete', { id }, true);
}

/** "Describe with AI": a suggested description for the asset (nothing is saved). */
export async function describeCustomizationAsset(id) {
  return await update('CustomizationAsset_Describe', { id }, true);
}

/**
 * The asset's thumbnail PNG (image / pdf page 1; svg and text have none) as a data URL, or null. The Download
 * controller needs the bearer token, so an <img src> cannot point at it directly; hash busts the browser cache
 * when the file is replaced.
 */
export async function fetchCustomizationAssetThumbnail(id, hash = null) {
  try {
    const token = await authService.getAccessToken();
    const url = `/Download/CustomizationAssetThumbnail?id=${encodeURIComponent(id)}${hash ? `&hash=${encodeURIComponent(hash)}` : ''}`;
    const response = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Downloads the asset's original file (image / pdf; Download/CustomizationAssetFile) with its original filename. */
export async function downloadCustomizationAssetFile(asset) {
  if (!asset?.ID || !asset?.DocumentID) return;
  const token = await authService.getAccessToken();
  const response = await fetch(`/Download/CustomizationAssetFile?id=${encodeURIComponent(asset.ID)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`.trim());
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = asset.FileName || `${asset.Key}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
}

// Customization packages (.cust): export a selection with its assets / data store rows, analyse and import one
// (CustomizationPackageUpdates.cs). The import is deterministic - the package's manifest carries the assessment.

const exportRequest = (model) => ({
  customizationIDs: (model?.customizationIDs ?? []).map(Number).filter((n) => n > 0),
  assetKeys: model?.assetKeys ?? [],
  dataStoreRows: (model?.dataStoreRows ?? []).map((r) => ({ scope: r.scope ?? r.Scope ?? '', key: r.key ?? r.Key ?? '' })),
  preferPublished: model?.preferPublished !== false,
  includeDataStoreValues: model?.includeDataStoreValues !== false,
  analyze: model?.analyze !== false,
});

/**
 * What a selection pulls in before the file is built: { Assets: [{ Key, Exists, Kind, Description, SizeBytes, Active, UsedBy }],
 * DataStoreRows: [{ Scope, Key, Reference, Exists, IsEncrypted, IsWildcard, Description, UsedBy }], Notes: [{ CustomizationID, Name, Note }] }.
 */
export async function getCustomizationExportDependencies(model) {
  const res = await update('Customization_ExportDependencies', exportRequest(model), false);
  if (isFrameworkError(res)) throw new Error(typeof res?.Message === 'string' ? res.Message : 'The export could not be prepared.');
  return { Assets: res?.Assets ?? [], DataStoreRows: res?.DataStoreRows ?? [], Notes: res?.Notes ?? [] };
}

/**
 * Starts the export as a detached server job (the AI assessment can run past the request timeout) and returns its
 * token. Poll getCustomizationExportStatus until State is 'complete', then downloadCustomizationPackage(token).
 */
export async function startCustomizationPackageExport(model) {
  const res = await update('Customization_StartExportPackage', exportRequest(model), false);
  if (isFrameworkError(res)) throw new Error(typeof res?.Message === 'string' ? res.Message : 'The export could not be started.');
  if (res?.Error) throw new Error(res.Error);
  if (!res?.Token) throw new Error('The export could not be started.');
  return res.Token;
}

/** { Token, State: 'queued'|'running'|'complete'|'failed', Stage: 'scan'|'assess'|'package', Done, Total, Current, FileName, SizeBytes, AnalysisLevel, Error }. */
export async function getCustomizationExportStatus(token) {
  const res = await update('Customization_ExportStatus', { token }, false);
  if (isFrameworkError(res)) throw new Error(typeof res?.Message === 'string' ? res.Message : 'The export status could not be read.');
  return res ?? { State: 'failed', Error: 'The export status could not be read.' };
}

/** Downloads a finished export (one shot - the server removes the job once taken). Resolves to the file name. */
export async function downloadCustomizationPackage(token, fallbackName) {
  const accessToken = await authService.getAccessToken();
  const response = await fetch(`/Download/CustomizationPackage?token=${encodeURIComponent(token)}`, { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
  if (!response.ok) throw new Error(response.status === 404 ? 'The export file is no longer available. Start it again.' : `${response.status} ${response.statusText}`.trim());
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const fileName = match ? decodeURIComponent(match[1]) : (fallbackName || 'Customizations.cust');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
  return fileName;
}

/** Reads a .cust file against this system: { Success, Error, Customizations, Assets, DataStoreRows, References, ... }. */
export async function analyzeCustomizationImportPackage(file) {
  return await updateAsFormData('Customization_AnalyzeImportPackage', { file });
}

/** Applies the user's choices: { Success, Error, Customizations, Assets, DataStoreRows } with per-item outcomes. */
export async function importCustomizationPackage(file, model) {
  return await updateAsFormData('Customization_ImportPackage', { file, modelJson: JSON.stringify(model ?? {}) });
}

/** The destination system's candidates for a reference kind (shared Import_ReferenceSelectionList endpoint). */
export async function getImportReferenceSelectionList(model) {
  return await update('Import_ReferenceSelectionList', model ?? {}, false);
}

/**
 * "Send Chat as Bug": emails the assistant session (description + transcript +
 * server-side diagnostic capture as an expiring log link) to the tenant's
 * configured support address. Returns { sent, error }.
 */
export async function reportAssistantBug({ diagnosticSessionID, description, hostLabel, transcript }) {
  const res = await update('Assistant_ReportBug', { diagnosticSessionID, description, hostLabel, transcript }, true);
  return {
    sent: !!(res?.Sent ?? res?.sent),
    error: (res?.Error ?? res?.error ?? '').toString(),
  };
}
